import {
  type BN,
  buildStageOutcomeMetadata,
  fetchStagedOutcomeMetadata,
  type StagedOutcomeMetadata,
} from '@morfi/contract-sdk';
import type { Connection, PublicKey } from '@solana/web3.js';
import type { TxEngineLogger } from '../tx-engine/config.js';
import {
  createTransactionTraceId,
  hashDiagnosticEntity,
} from '../tx-engine/diagnostics/lifecycle.js';
import type { SubmissionOptions } from '../tx-engine/types.js';
import { sleepOrAbort, throwIfAborted } from './orchestrator-abort.js';
import { type SubmitTransactionFn, toEngineBuildResult } from './orchestrator-build.js';
import {
  buildWithFailureLog,
  stageMetadata,
  submissionFailureLog,
} from './orchestrator-diagnostics.js';
import {
  isTransientInfraFailure,
  SUBMISSION_MAX_RETRIES,
  submissionRetryDelayMs,
} from './retry-submission.js';
import { txSubmitError } from './tx-submit-error.js';
import type { CreateMarketOutcomeParams } from './types.js';

interface StagingArgs {
  connection: Connection;
  submitTransaction: SubmitTransactionFn;
  creator: PublicKey;
  payer: PublicKey;
  market: PublicKey;
  marketNonce: BN;
  outcomes: readonly CreateMarketOutcomeParams[];
  baseOpts: Omit<SubmissionOptions, 'instructionName'>;
  relayFeePayerBase58: string;
  baseMetadata: NonNullable<SubmissionOptions['metadata']>;
  marketCreationTraceId: string;
  signal: AbortSignal | undefined;
  logger: TxEngineLogger | undefined;
}

export async function stageInitialOutcomeMetadata(args: StagingArgs): Promise<void> {
  let stagedCount = await readExactPrefix(args);
  for (let index = stagedCount; index < args.outcomes.length; index += 1) {
    let retries = 0;
    const outcome = args.outcomes[index];
    if (outcome === undefined) throw new Error('Missing initial outcome tuple');
    const metadata = stageMetadata(args.baseMetadata, 'preparing', 'stage_outcome_metadata', {
      outcomeIndex: index,
      outcomeTickerHash: hashDiagnosticEntity(outcome.ticker),
    });
    const build = await buildWithFailureLog({
      logger: args.logger,
      metadata,
      build: () =>
        buildStageOutcomeMetadata(args.connection, {
          creator: args.creator,
          payer: args.payer,
          marketNonce: args.marketNonce,
          initialOutcomes: args.outcomes.length,
          outcomes: [
            { outcomeIndex: index, name: outcome.name, symbol: outcome.symbol, uri: outcome.uri },
          ],
        }),
    });
    const initialTraceId = createTransactionTraceId();
    for (;;) {
      throwIfAborted(args.signal);
      const traceId = retries === 0 ? initialTraceId : createTransactionTraceId();
      const result = await args.submitTransaction(toEngineBuildResult(build), {
        ...args.baseOpts,
        instructionName: 'stage_outcome_metadata',
        traceId,
        metadata: { ...metadata, txTraceId: traceId },
        forceRelay: true,
        expectedRelayFeePayer: args.relayFeePayerBase58,
        attemptContext:
          retries === 0
            ? { workflow: 'market_creation', workflowTraceId: args.marketCreationTraceId }
            : {
                workflow: 'market_creation',
                workflowTraceId: args.marketCreationTraceId,
                initialTraceId,
                retryAttempt: retries,
              },
      });
      if (result.ok) {
        stagedCount = index + 1;
        break;
      }
      stagedCount = await readExactPrefix(args);
      if (stagedCount > index) break;
      if (!isTransientInfraFailure(result) || retries >= SUBMISSION_MAX_RETRIES) {
        args.logger?.error(
          'market_creation.metadata_stage_failed',
          submissionFailureLog(metadata, result),
        );
        throw txSubmitError(
          `stage_outcome_metadata[${String(index)}] failed: ${result.error.morfiError ?? result.error.userMessageKey ?? result.error.code}`,
          result.error,
          submissionFailureLog(metadata, result),
        );
      }
      retries += 1;
      const delayMs = submissionRetryDelayMs(retries);
      args.logger?.warn('market_creation.stage_retry_scheduled', {
        ...submissionFailureLog(metadata, result),
        attempt: retries,
        delayMs,
      });
      await sleepOrAbort(delayMs, args.signal);
      // A prior relay attempt may have landed during the backoff.
      stagedCount = await readExactPrefix(args);
      if (stagedCount > index) break;
    }
    index = Math.max(index, stagedCount - 1);
  }
}

async function readExactPrefix(args: StagingArgs): Promise<number> {
  try {
    const metadata = await fetchStagedOutcomeMetadata(args.connection, {
      market: args.market,
      creator: args.creator,
      marketNonce: args.marketNonce,
      initialOutcomes: args.outcomes.length,
    });
    if (metadata === null) return 0;
    assertExactPrefix(metadata, args.outcomes);
    return metadata.outcomes.length;
  } catch (error) {
    args.logger?.warn('market_creation.staged_metadata_read_failed', {
      marketHash: hashDiagnosticEntity(args.market.toBase58()),
    });
    throw error;
  }
}

function assertExactPrefix(
  metadata: StagedOutcomeMetadata,
  expected: readonly CreateMarketOutcomeParams[],
): void {
  if (metadata.outcomes.length > expected.length)
    throw new Error('Existing staged outcome prefix exceeds request');
  for (const [index, staged] of metadata.outcomes.entries()) {
    const tuple = expected[index];
    if (
      tuple === undefined ||
      staged.outcomeIndex !== index ||
      staged.name !== tuple.name ||
      staged.symbol !== tuple.symbol ||
      staged.uri !== tuple.uri
    )
      throw new Error('Existing staged outcome tuple differs from request');
  }
}
